import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { BACKEND_URL } from '../../utils/config';
import { logout } from '../slices/authSlice';

// Check if backend is v5 (currently local port 1338 is v5, or explicit env flag)
const isV5 = BACKEND_URL.includes('1338') || process.env.REACT_APP_IS_STRAPI_V5 === 'true';
const apiPrefix = isV5 ? '/api' : '';

// Create a custom base query with error handling
const baseQueryWithReauth = fetchBaseQuery({
  baseUrl: `${BACKEND_URL}${apiPrefix}`,
  credentials: 'include',
  prepareHeaders: (headers, { getState, endpoint, extraOptions }) => {
    // Identify public endpoints based on URL pattern
    if (isPublicEndpoint(endpoint)) {
      // For public endpoints, we don't need to add authentication headers
      return headers;
    }

    const token = getState()?.auth?.token || localStorage.getItem('token');

    // Add auth header if token exists
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    // Ensure proper content-type for JSON requests (if not FormData)
    if (!(extraOptions?.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }

    return headers;
  }
});

const baseQueryWithErrorHandling = async (args, api, extraOptions) => {
  // For FormData, make sure we don't mess with the Content-Type
  // The browser will set it automatically with the correct boundary
  if (args.body instanceof FormData) {
    // Remove any existing Content-Type header to let the browser set it
    if (args.headers) {
      delete args.headers['Content-Type'];
    }
  }

  // Intercept for demo account
  const state = api.getState();
  const isDemo = state.auth?.user?.username === 'demo';
  let modifiedArgs = args;
  
  if (isDemo) {
    if (typeof args === 'string') {
      if (args.includes('/products')) {
        modifiedArgs = args.replace('/products', '/demo-products');
      } else if (args.includes('/listings')) {
        modifiedArgs = args.replace('/listings', '/demo-products');
      }
    } else if (args && args.url) {
      if (args.url.includes('/products')) {
        modifiedArgs = { ...args, url: args.url.replace('/products', '/demo-products') };
      } else if (args.url.includes('/listings')) {
        modifiedArgs = { ...args, url: args.url.replace('/listings', '/demo-products') };
      }
    }
  }

  try {
    const result = await baseQueryWithReauth(modifiedArgs, api, extraOptions);

    if (result.error) {
      // Check if this is a public endpoint (based on URL pattern)
      const isPublicEndpointRequest = modifiedArgs.url ? isPublicEndpoint(modifiedArgs.url) : (typeof modifiedArgs === 'string' && isPublicEndpoint(modifiedArgs));

      // Handle authentication errors (except for public endpoints)
      if (result.error.status === 401 && !isPublicEndpointRequest) {
        // Auth error - clear credentials
        api.dispatch(logout());
      }
    }

    return result;
  } catch (error) {
    console.error('API request failed:', error);
    return { error: { status: 'FETCH_ERROR', error: error.message } };
  }
};

export const createBaseApi = (reducerPath) => {
  return createApi({
    reducerPath,
    baseQuery: baseQueryWithErrorHandling,
    tagTypes: ['Auth', 'User', 'Product', 'Listing', 'Review', 'Profile', 'Address', 'Upload', 'Cart', 'Chats', 'Messages', 'UnreadCount', 'Merchant', 'Order', 'FulfillmentGroup', 'OrderItem', 'Request', 'Delivery', 'UserReviewsGiven', 'UserReviewsReceived', 'ExistingCustomerReview', 'ExistingOrderCustomerReview', 'InCloset', 'Closets', 'Credits', 'MapDrops', 'License', 'Offer', 'VtoResult'],
    endpoints: () => ({}),
    keepUnusedDataFor: 60, // Keep unused data in cache for 60 seconds
    refetchOnMountOrArgChange: 300, // Refetch after 5 minutes
    refetchOnFocus: false,
    refetchOnReconnect: true
  });
};

export const createProtectedEndpoint = (builder, endpoint) => {
  return builder.query({
    ...endpoint,
    transformErrorResponse: (response) => {
      if (response.status === 401) {
        // Handle unauthorized access
        return { error: 'Unauthorized access' };
      }
      return response;
    }
  });
};

export const handleWebhookUpdate = (store, tags = ['Product']) => {
  tags.forEach(tag => {
    Object.values(store.dispatch)
      .filter(value => typeof value === 'object' && value.util)
      .forEach(api => {
        store.dispatch(api.util.invalidateTags([tag]));
      });
  });
};

// List of endpoints that don't require authentication
const isPublicEndpoint = (endpoint) => {
  const ep = endpoint.toLowerCase();
  return (
    ep.includes('login') ||
    ep.includes('register') ||
    ep.includes('forgot-password') ||
    ep.includes('reset-password') ||
    ep.includes('social-auth-url') ||
    ep.includes('verify-email') ||
    ep.includes('home-feature') ||
    ep.includes('homefeature') ||
    ep.includes('launch-config') ||
    ep.includes('launchconfig') ||
    ep.includes('sheyou-features')
  );
};
